/**
 * Utility functions for handling targeted account restrictions and operations
 */

/**
 * Validates and filters redirect account restrictions
 * @param {Array} redirectAccountRestrictions - Array of account restrictions
 * @returns {Array|null} - Validated restrictions or null if invalid
 */
export const redirectAccountRestrictionsValidator = (redirectAccountRestrictions) => {
    if (!Array.isArray(redirectAccountRestrictions)) {
        return null;
    }

    const validListofAccounts = redirectAccountRestrictions.filter((item) => {
        if (item == null) return false;

        const requiredKeys = ['fipId', 'fiType', 'accType', 'maskedAccNumber'];

        // Check required keys (excluding restrictions)
        for (let key of requiredKeys) {
            if (!(key in item) || item[key] == null) return false;
        }

        for (let key of ['fipId', 'fiType', 'accType', 'maskedAccNumber']) {
            const value = item[key];
            if (typeof value !== 'string' || value.trim() === '') return false;
        }

        // Check restrictions field - allow null or valid object
        if ('restrictions' in item) {
            const restrictions = item.restrictions;
            
            // Allow null restrictions
            if (restrictions === null) return true;
            
            // If restrictions exist, validate them
            if (typeof restrictions !== 'object') return false;

            if (restrictions.isMandatory !== undefined) {
                if (typeof restrictions.isMandatory !== 'string') return false;
                
                const normalized = restrictions.isMandatory.trim().toLowerCase();
                if (normalized !== 'true' && normalized !== 'false') return false;
            }
        }

        return true;
    });

    return validListofAccounts.length > 0 ? validListofAccounts : null;
};

/**
 * Filters linked accounts based on redirect account restrictions
 * @param {Array} linkedAccounts - Array of linked accounts
 * @returns {Array} - Filtered accounts that match restrictions
 */
export const filterLinkedAccountsByRestrictions = (linkedAccounts) => {
    if (!global.redirectAccountRestrictions) {
        return linkedAccounts;
    }

    const filteredAccounts = linkedAccounts.filter((account) => {
        const matches = global.redirectAccountRestrictions.some((restriction) => {
            const isMatch =
                account.FIType === restriction.fiType &&
                account.accType === restriction.accType &&
                account.maskedAccNumber?.slice(-4) === restriction.maskedAccNumber;
            return isMatch;
        });
        return matches;
    });
    return filteredAccounts;
};

/**
 * Checks if an account is mandatory based on redirect restrictions
 * @param {Object} account - Account object to check
 * @returns {boolean} - True if account is mandatory
 */
export const isAccountMandatory = (account) => {
    if (!global.redirectAccountRestrictions) {
        return false;
    }

    const restriction = global.redirectAccountRestrictions.find(
        (restriction) =>
            restriction.fiType === account.FIType &&
            restriction.accType === account.accType &&
            restriction.maskedAccNumber === account.maskedAccNumber?.slice(-4)
    );

    return restriction?.restrictions?.isMandatory?.toLowerCase() === 'true';
};

/**
 * Checks if all mandatory accounts are linked
 * @param {Array} displayedAccounts - Array of discovered accounts by FIP
 * @param {Array} linkedAccounts - Array of linked accounts
 * @returns {boolean} - True if all mandatory accounts are linked
 */
export const isAllMandatoryAccountsLinked = (displayedAccounts, linkedAccounts) => {
    const allDiscoveredAccounts = [];
    displayedAccounts.forEach((fip) => {
        if (fip.discoveredAccounts) {
            allDiscoveredAccounts.push(...fip.discoveredAccounts);
        }
    });
    
    const mandatoryRestrictions = allDiscoveredAccounts.filter(
        (account) => account?.restrictions?.isMandatory?.toLowerCase() === "true"
    );
    
    const allMandatoryAccountsLinked = mandatoryRestrictions.every((restriction) =>
        linkedAccounts.some(
            (account) =>
                account.FIType === restriction.FIType &&
                account.accType === restriction.accType &&
                account.maskedAccNumber === restriction.maskedAccNumber
        )
    );
    
    return allMandatoryAccountsLinked;
};

/**
 * Filters selected accounts based on redirect restrictions for consent processing
 * @param {Array} selectedAccounts - Array of selected accounts
 * @returns {Array} - Filtered accounts that match restrictions
 */
export const filterSelectedAccountsByRestrictions = (selectedAccounts) => {
    if (!global.redirectAccountRestrictions) {
        return selectedAccounts;
    }

    return selectedAccounts.filter((account) => {
        return global.redirectAccountRestrictions.some(
            (restriction) =>
                restriction.fiType === account.FIType &&
                restriction.accType === account.accType &&
                restriction.maskedAccNumber === account.maskedAccNumber.slice(-4)
        );
    });
};

/**
 * Checks if all mandatory restrictions are satisfied by linked accounts
 * @param {Array} linkedAccounts - Array of linked accounts
 * @returns {boolean} - True if all mandatory restrictions are satisfied
 */
export const areAllMandatoryRestrictionsSatisfied = (linkedAccounts) => {
    if (!global.redirectAccountRestrictions) {
        return true;
    }

    return global.redirectAccountRestrictions.every(
        (restriction) =>
            restriction.restrictions?.isMandatory?.toLowerCase() !== 'true' ||
            linkedAccounts.some(
                (account) =>
                    account.FIType?.slice(-4) === restriction.fiType?.slice(-4) &&
                    account.accType === restriction.accType &&
                    account.maskedAccNumber?.slice(-4) === restriction.maskedAccNumber
            )
    );
};

/**
 * Checks if account restrictions are satisfied by discovered accounts
 * @param {Array} discoveredAccounts - Array of discovered accounts
 * @param {Array} linkedAccounts - Array of linked accounts
 * @returns {boolean} - True if restrictions are satisfied
 */
export const areAccountRestrictionsSatisfied = (discoveredAccounts, linkedAccounts) => {
    if (!global.redirectAccountRestrictions) {
        return true;
    }

    return global.redirectAccountRestrictions.every((restriction) =>
        linkedAccounts.some(
            (account) =>
                account.FIType === restriction.fiType &&
                account.accType === restriction.accType &&
                account.maskedAccNumber?.slice(-4) === restriction.maskedAccNumber
        )
    );
};

/**
 * Filters discovered accounts to only include targeted accounts
 * @param {Array} fullDiscoveredAccounts - Array of all discovered accounts by FIP
 * @param {Array} targetedAccounts - Array of targeted account restrictions
 * @returns {Array} - Filtered discovered accounts
 */
export const getTargetedAccounts = (fullDiscoveredAccounts, targetedAccounts) => {
    // Create a set of targeted account identifiers for quick lookup
    const targetedSet = new Set(
        targetedAccounts.map(
            (account) =>
                `${account.fipId}-${account.fiType}-${account.accType}-${account.maskedAccNumber}`
        )
    );
    
    // Filter the fullDiscoveredAccounts based on the targeted accounts
    const result = fullDiscoveredAccounts
        .map((fip) => {
            // Filter the discovered accounts for the current FIP
            const filteredAccounts = fip.discoveredAccounts
                .filter((account) => {
                    const accountIdentifier = `${fip.fipId}-${account.FIType}-${
                        account.accType
                    }-${account.maskedAccNumber?.slice(-4)}`;
                    const isTargeted = targetedSet.has(accountIdentifier);
                    return isTargeted;
                })
                .map((account) => {
                    const targetedAccount = targetedAccounts.find(
                        (target) =>
                            target.fipId === fip.fipId &&
                            target.fiType === account.FIType &&
                            target.accType === account.accType &&
                            target.maskedAccNumber === account.maskedAccNumber?.slice(-4)
                    );

                    const mappedAccount = targetedAccount
                        ? { ...account, restrictions: targetedAccount.restrictions }
                        : account;
                    return mappedAccount;
                });
            
            // Return the FIP object with filtered accounts
            return {
                ...fip,
                discoveredAccounts: filteredAccounts,
            };
        })
        .filter((fip) => fip.discoveredAccounts.length > 0); // Only include FIPs with accounts

    return result;
}; 